import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { AuthService } from "../../../main";
import { environment } from '../../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api/menuitem';
import { ArkUserType, UserModel } from '../../../core/api/models';
import { AlertService } from '../../services/alert.service';
import { SwUpdate } from '@angular/service-worker';
import { DomSanitizer } from '@angular/platform-browser';


@Component({
  selector: 'top-bar',
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.css'],
  standalone: false
})
export class TopBarComponent implements OnInit {
  userInfo: UserModel | null = null;
  items: MenuItem[] | undefined = undefined;

  deferredPrompt: any;

  hasServiceWorker: boolean = false;
  versionInfo: string | null  = null;

  alertService = inject(AlertService);

  loading: boolean = true;
  production: boolean = environment.production;
  environmentName: string = environment.name;

  constructor(
    private swUpdate: SwUpdate,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) {
  }

  get getProfileImageURL() {
    if (!this.userInfo || !this.userInfo.ProfileImageURL)
      return null;
    return this.userInfo.ProfileImageURL;
  }

  
  ngOnInit() {
    if (this.authService.isSignedIn()) 
      this.userInfo = this.authService.userInfo;

    this.authService.userInfo$.forEach((userInfo) => {
      this.userInfo = userInfo ;
      this.loadMenuItems();
    });
    

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        this.deferredPrompt = e as any;

        this.loadMenuItems();
      });
    }


    if (this.swUpdate.isEnabled) {
      this.hasServiceWorker = true;
      this.swUpdate.versionUpdates.subscribe((evt) => {
        switch (evt.type) {
          case 'VERSION_DETECTED':
            // 	Emitted when the service worker has detected a new version of the app on the server and is about to start downloading it.
            console.log(`Downloading new app version: ${evt.version.hash}`);
            //this.versionInfo = evt.version.hash;
            this.alertService.info(`There is a new version of the ArkMed Management. Downloading new app version: ${evt.version.hash}`);
            break;
          case 'VERSION_READY':
            //	Emitted when a new version of the app is available to be activated by clients. It may be used to notify the user of an available update or prompt them to refresh the page.
            console.log(`Current app version: ${evt.currentVersion.hash}`);
            console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
            this.alertService.info(`There is a new version of the ArkMed Management. Installing app version: ${evt.latestVersion.hash}`);
            //this.versionInfo = evt.latestVersion.hash;
            this.swUpdate.activateUpdate().then(s => {
              this.alertService.clear();
              //if (!s)
              window.location.reload();
            });
            break;
          case 'VERSION_INSTALLATION_FAILED':
            // Emitted when the installation of a new version failed. It may be used for logging/monitoring purposes.
            console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
            this.alertService.error(`Failed to install ArkMed Management version '${evt.version.hash}': ${evt.error}`);
            window.location.reload();
            break;
          case 'NO_NEW_VERSION_DETECTED':
            	// Emitted when the service worker has checked the version of the app on the server and did not find a new version.
            //console.log(`Lastest app version: ${evt.version.hash}`);
            this.versionInfo = evt.version.hash;
            break;
        }
      });

      this.swUpdate.checkForUpdate().then(v => {
        if (v) {
          this.alertService.info("There is a new version of the ArkMed Management.");
        }
      });

    }


    this.loadMenuItems();

  }

  installApp() {
    this.deferredPrompt.prompt();


    (this.deferredPrompt.userChoice as Promise<{
      outcome: 'accepted' | 'dismissed',
      platform: string
    }>).then(choiceResult => {
      if (choiceResult.outcome === 'accepted') {
        console.log('App installed');
      } else {
        console.log('App installation declined');
      }


    });
  }

  signOut() {
    this.authService.signOut().subscribe(() => {
      this.router.navigate(['/signin']);
    });
  }


  loadMenuItems() {

    if (this.userInfo == null) {
      this.items = [
        {
          label: 'Login',
          icon: 'pi pi-user',
          routerLink: ['/signin']
        }];

      return;
    }
    if (this.authService.tokenInfo?.refresh_token == null) {
      this.items = [
        {
          label: 'Verify 2 Factor',
          icon: 'pi pi-lock-open',
          routerLink: ['/profile']
        },
        {
          label: 'Logout',
          icon: 'pi pi-sign-out',
          command: () => this.signOut()
        }];

      return;
    }
    if (this.userInfo.PasswordExpireDate != null
      && new Date(this.userInfo.PasswordExpireDate) < new Date() ) {
      this.items = [
        {
          label: 'Password Expired',
          icon: 'pi pi-key',
          routerLink: ['/profile']
        },
        {
          label: 'Logout',
          icon: 'pi pi-sign-out',
          command: () => this.signOut()
        }];

      return;
    }
    if (this.userInfo.PasswordExpireDate != null
      && new Date(this.userInfo.PasswordExpireDate) < new Date() ) {
      this.items = [
        {
          label: 'Password Expired',
          icon: 'pi pi-key',
          routerLink: ['/profile']
        }];

      return;
    }
    var clients: Array<any> = [];

    /*
    if (this.clientsService.clients && this.clientsService.patients.length > 0 && this.patientsService.patients.length <= 10) {
      patients = [... this.patientsService.patients.map(patient => ({
        label: patient.firstName + ' ' + patient.lastName,
        icon: 'fal fa-fw fa-user',
        routerLink: [`/patient/${patient.inquiryID}`]
      }))];
    }
    */
    var clientsItems = this.authService.hasUserType(ArkUserType.Support)
      || this.authService.hasUserType(ArkUserType.Admin)
      || this.authService.hasUserType(ArkUserType.Impersonator) ? [

      {
        label: 'Clients',
        icon: 'pi pi-building-columns',
        routerLink: ['/clients']
      }] : [];

  
    var marketingItems = this.authService.hasUserType(ArkUserType.Marketing)
      || this.authService.hasUserType(ArkUserType.Admin) ? [

      {
          label: 'Marketing',
          icon: 'pi pi-dollar',
        items: [
          {
            label: 'Potencial Clients',
            icon: 'pi pi-star-half',
            routerLink: ['/clients/potencial']
          },
          {
            label: 'Survey',
            icon: 'pi pi-envelope',
            routerLink: ['/clients/Survey']
          },
          {
            label: 'Knowledge Base System',
            icon: 'pi pi-book',
            routerLink: ['/knowledge']
          }
        ]
      }] : [];
    var supportItems = this.authService.hasUserType(ArkUserType.Support)
      || this.authService.hasUserType(ArkUserType.Admin) ? [

      {
          label: 'Support',
          icon: 'pi pi-wrench',
        items: [
          {
            label: 'Tickets',
            icon: 'pi pi-hashtag',
            routerLink: ['/tickets']
          },
          {
            label: 'Release Notes',
            icon: 'pi pi-file-edit',
            routerLink: ['/releasenotes']
          },
          {
            label: 'Features / Settings',
            icon: 'pi pi-briefcase',
            routerLink: ['/features']
          },
          {
            label: 'Permission',
            icon: 'pi pi-key',
            routerLink: ['/permission']
          },
          {
            label: 'Navigation',
            icon: 'pi pi-sitemap',
            routerLink: ['/navigation/links']
          },
        ]
      }] : [];
    var developerItem = this.authService.hasUserType(ArkUserType.Developer)
      || this.authService.hasUserType(ArkUserType.Admin) ? [

        {
          // pi-server
          // pi-save
          // pi-power-off

        label: 'Developer',
          icon: 'pi pi-code',
        items: [
          {
            label: 'System Status',
            icon: 'pi pi-desktop',
            routerLink: ['/systemstatus/']
          },
          {
            label: 'Error log',
            icon: 'pi pi-exclamation-triangle',
            routerLink: ['/exceptionlog/']
          },
          {
            label: 'Web Sites',
            icon: 'pi pi-globe',
            routerLink: ['/developer/websites']
          },
          {
            label: 'On Demand Tasks',
            icon: 'pi pi-play',
            routerLink: ['/developer/ondemandtasks']
          },
          {
            label: 'Backups',
            icon: 'pi pi-save',
            routerLink: ['/developer/backups']
          },
          {
            label: 'Applied Patches',
            icon: 'pi pi-list',
            routerLink: ['/developer/appliedpatches']
          }]
      }]: [];

    var adminItems = this.authService.hasUserType(ArkUserType.Admin) ? [
      {
        label: 'Admin',
        icon: 'pi pi-cog',
        items: [{
          label: 'Ark Users',
          icon: 'pi pi-users',
          routerLink: ['/users']
        },
        {
          label: 'Ticket Statuses',
          icon: 'pi pi-list-check',
          routerLink: ['/ticket/status']
        },
        {
          label: 'AI Model Pricing',
          icon: 'pi pi-dollar',
          routerLink: ['/ai-model-pricing']
        }]
      }] : [];


    var appItems = this.hasServiceWorker && this.deferredPrompt ? [
      {
        label: 'Install app',
        icon: 'pi pi-download',
        command: () => this.installApp()
      }] : [];
    

    this.items = [
      ...clientsItems,
      ...marketingItems,
      ...supportItems,
      ...developerItem,
      ...adminItems,
      {
        label: 'Account',
        icon: 'pi pi-home',
        items: [{
          label: 'Profile / Settings',
          icon: 'pi pi-user',
          routerLink: ['/profile']
        },
          ...appItems,
        {
          label: 'Logout',
          icon: 'pi pi-sign-out',
          command: () => this.signOut()
        }
        ]
      }];

  }

  profileInitials() {
    if (!this.userInfo)
      return null;
    if (this.userInfo?.FirstName && this.userInfo?.FirstName.length > 0
      && this.userInfo?.LastName && this.userInfo?.LastName.length > 0)
      return (this.userInfo?.FirstName[0] + this.userInfo?.LastName[0]).toUpperCase();

    var temp = this.userInfo?.FirstName || this.userInfo?.UserName;
    if (temp && temp.length > 1)
      return temp[0].toUpperCase();
    return null;
  }

  clickItem(item: any) {
    console.debug('clickItem', item);
    $('#offcanvasNavbarClose').click();

    //delay(200).then(() => {
    //  console.debug('clickItem done', item);
    if (!!item.routerLink)
      this.router.navigate(item.routerLink,);
    else if (!!item.command)
      item.command.apply(this);

    //});
    return false;

  }
  itemLink(item: any) {
    
    
    if (!!item.routerLink)
       return item.routerLink
    else 
      return this.sanitizer.bypassSecurityTrustUrl("javascript:void()");

  }
}
